import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import './AdminSubjectManagementView.css';
import './AdminMoreDetailPages.css';
import { docInScope, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

// Match iOS SubjectCategory enum exactly (Subject.swift)
const SUBJECT_CATEGORIES = [
  'Principles of Accounts (POA)',
  'Agriculture',
  'Arts',
  'Biology',
  'Building Technology',
  'Principles of Business (POB)',
  'Chemistry',
  'English',
  'Foreign Language',
  'French',
  'History',
  'Home Economics',
  'Information Technology',
  'Mathematics',
  'Penmanship',
  'Physical Education',
  'Reading Comprehension',
  'Science',
  'Social Studies',
  'Spanish',
  'Woodwork',
  'Other',
];

// Web emoji equivalents of iOS SF Symbol icons (Subject.swift)
const CATEGORY_ICONS = {
  'Mathematics': '�',
  'English': '📖',
  'Science': '🧪',
  'Biology': '🌱',
  'Chemistry': '⚗️',
  'Agriculture': '🌾',
  'Principles of Accounts (POA)': '�',
  'Principles of Business (POB)': '💼',
  'Social Studies': '🌍',
  'History': '�',
  'Arts': '🎨',
  'Physical Education': '🏃',
  'Foreign Language': '💬',
  'Spanish': '🇪🇸',
  'French': '🇫🇷',
  'Information Technology': '💻',
  'Building Technology': '🔨',
  'Home Economics': '🏠',
  'Woodwork': '🪵',
  'Reading Comprehension': '📚',
  'Penmanship': '✏️',
  'Other': '⭐',
};

function AdminSubjectManagementView({ adminData }) {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSubject, setEditingSubject] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [newSubject, setNewSubject] = useState({
    name: '',
    category: 'Core',
    description: '',
    isActive: true,
  });
  const [editForm, setEditForm] = useState({
    name: '',
    category: 'Core',
    description: '',
    isActive: true,
  });

  const loadSubjects = useCallback(async () => {
    setLoading(true);
    try {
      const schoolId = adminData?.schoolId;
      if (!schoolId) {
        setError('No school assigned. Contact your Super Admin.');
        setLoading(false);
        return;
      }
      const ids = adminSchoolIds(adminData);
      const allSubjects = [];
      const seenIds = new Set();

      const ck = (p) => `${ids.join('|') || 'all'}:${p}`;

      // 1. Load from top-level subjects collection (school-wide / global subjects)
      const globalDocs = (await dataCacheService.getDocs(ck('subjects'), collection(db, 'subjects')))
        .filter(d => docInScope(d.schoolId, ids));
      globalDocs.forEach(data => {
        if (!seenIds.has(data.id)) { seenIds.add(data.id); allSubjects.push(data); }
      });

      // 2. Load from classes/{classId}/subjects subcollections (parallel, cached)
      const classDocs = (await dataCacheService.getDocs(ck('classes'), collection(db, 'classes')))
        .filter(d => docInScope(d.schoolId, ids));
      const classSubDocs = await Promise.all(classDocs.map(c =>
        dataCacheService.getDocs(ck(`classes/${c.id}/subjects`), collection(db, 'classes', c.id, 'subjects'))
      ));
      classSubDocs.flat().forEach(data => {
        if (!seenIds.has(data.id)) { seenIds.add(data.id); allSubjects.push(data); }
      });

      // 3. Load from gradeLevels/{gradeId}/subjects subcollections (parallel, cached)
      const gradeDocs = (await dataCacheService.getDocs(ck('gradeLevels'), collection(db, 'gradeLevels')))
        .filter(d => docInScope(d.schoolId, ids));
      const gradeSubDocs = await Promise.all(gradeDocs.map(g =>
        dataCacheService.getDocs(ck(`gradeLevels/${g.id}/subjects`), collection(db, 'gradeLevels', g.id, 'subjects'))
      ));
      gradeSubDocs.flat().forEach(data => {
        if (!seenIds.has(data.id)) { seenIds.add(data.id); allSubjects.push(data); }
      });

      setSubjects(allSubjects);
    } catch (err) {
      console.error('Error loading subjects:', err);
      setError('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, [adminData?.schoolId]);

  useEffect(() => {
    loadSubjects();
  }, [loadSubjects]);

  const handleAddSubject = async (e) => {
    e.preventDefault();
    if (!newSubject.name.trim()) {
      setError('Subject name is required');
      return;
    }
    try {
      const subjectId = crypto.randomUUID();
      const subjectData = {
        id: subjectId,
        schoolId: adminData.schoolId,
        name: newSubject.name.trim(),
        category: newSubject.category,
        description: newSubject.description.trim(),
        isActive: newSubject.isActive,
        createdBy: adminData?.uid || '',
        createdAt: new Date().toISOString(),
      };
      await setDoc(doc(db, 'subjects', subjectId), subjectData);
      dataCacheService.invalidatePath('subjects');
      setSubjects([...subjects, subjectData]);
      setNewSubject({ name: '', category: 'Core', description: '', isActive: true });
      setShowAddForm(false);
      setError('');
    } catch (err) {
      console.error('Error adding subject:', err);
      setError('Failed to add subject');
    }
  };

  const handleEditClick = (subject) => {
    setEditingSubject(subject);
    setEditForm({
      name: subject.name || '',
      category: subject.category || 'Core',
      description: subject.description || '',
      isActive: subject.isActive !== false,
    });
  };

  const handleSaveEdit = async () => {
    if (!editingSubject) return;
    if (!editForm.name.trim()) {
      setError('Subject name is required');
      return;
    }
    try {
      dataCacheService.invalidatePath('subjects');
      await updateDoc(doc(db, 'subjects', editingSubject.id), {
        name: editForm.name.trim(),
        category: editForm.category,
        description: editForm.description.trim(),
        isActive: editForm.isActive,
      });
      setSubjects(subjects.map(s =>
        s.id === editingSubject.id
          ? { ...s, name: editForm.name.trim(), category: editForm.category, description: editForm.description.trim(), isActive: editForm.isActive }
          : s
      ));
      setEditingSubject(null);
      setError('');
    } catch (err) {
      console.error('Error updating subject:', err);
      setError('Failed to update subject');
    }
  };

  const handleToggleActive = async (subject) => {
    try {
      const newActive = !subject.isActive;
      await updateDoc(doc(db, 'subjects', subject.id), { isActive: newActive });
      dataCacheService.invalidatePath('subjects');
      setSubjects(subjects.map(s =>
        s.id === subject.id ? { ...s, isActive: newActive } : s
      ));
    } catch (err) {
      console.error('Error toggling subject active state:', err);
      setError('Failed to update subject');
    }
  };

  const handleDeleteSubject = async (subject) => {
    if (!confirm(`Delete "${subject.name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'subjects', subject.id));
      dataCacheService.invalidatePath('subjects');
      setSubjects(subjects.filter(s => s.id !== subject.id));
    } catch (err) {
      console.error('Error deleting subject:', err);
      setError('Failed to delete subject');
    }
  };

  const getCategoryIcon = (category) => {
    return CATEGORY_ICONS[category] || '📖';
  };

  const filteredSubjects = subjects.filter(subject =>
    subject.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCount = subjects.filter(s => s.isActive !== false).length;
  const inactiveCount = subjects.length - activeCount;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // === Full-screen subject detail page ===
  if (selectedSubject) {
    const s = selectedSubject;
    console.log('🔥 AdminSubjects: Showing detail for subject', s.id, s);
    return (
      <div className="admin-subject-management-view detail-page">
        <button className="detail-back-button" onClick={() => setSelectedSubject(null)}>
          ◄ Back to Subjects
        </button>
        <div className="detail-card">
          <div className="detail-header">
            <div>
              <h2>{getCategoryIcon(s.category)} {s.name}</h2>
              <p className="detail-subtitle">{s.category || 'Subject'}</p>
            </div>
            <span className="detail-status-badge" style={{
              background: s.isActive !== false ? '#d1fae5' : '#f3f4f6',
              color: s.isActive !== false ? '#065f46' : '#6b7280'
            }}>
              {s.isActive !== false ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="detail-grid">
            <div className="detail-field">
              <span className="detail-label">Name</span>
              <span className="detail-value">{s.name || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Category</span>
              <span className="detail-value">{s.category || 'N/A'}</span>
            </div>
            <div className="detail-field">
              <span className="detail-label">Status</span>
              <span className="detail-value">{s.isActive !== false ? 'Active' : 'Inactive'}</span>
            </div>
            {s.createdAt && (
              <div className="detail-field">
                <span className="detail-label">Created</span>
                <span className="detail-value">
                  {s.createdAt.toDate ? s.createdAt.toDate().toLocaleDateString() : new Date(s.createdAt).toLocaleDateString()}
                </span>
              </div>
            )}
            {s.createdBy && (
              <div className="detail-field">
                <span className="detail-label">Created By</span>
                <span className="detail-value">{s.createdBy}</span>
              </div>
            )}
          </div>

          <div className="detail-section">
            <h4>Description</h4>
            {s.description ? (
              <p className="detail-text">{s.description}</p>
            ) : (
              <p className="detail-empty-text">No description provided</p>
            )}
          </div>

          <div className="detail-actions">
            <button className="detail-btn-primary" onClick={() => { handleEditClick(s); setSelectedSubject(null); }}>
              ✏️ Edit Subject
            </button>
            <button
              className="detail-btn-neutral"
              onClick={() => { handleToggleActive(s); setSelectedSubject({ ...s, isActive: !s.isActive }); }}
            >
              {s.isActive !== false ? '⏸️ Deactivate' : '✅ Activate'}
            </button>
            <button className="detail-btn-danger" onClick={async () => { await handleDeleteSubject(s); setSelectedSubject(null); }}>
              🗑️ Delete Subject
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-subject-management-view">
      <div className="view-header">
        <h2>Subjects</h2>
        <button className="add-button" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? 'Cancel' : '+ Add Subject'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📖</div>
          <div className="stat-info">
            <h3>{subjects.length}</h3>
            <p>Total Subjects</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <h3>{activeCount}</h3>
            <p>Active</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⏸️</div>
          <div className="stat-info">
            <h3>{inactiveCount}</h3>
            <p>Inactive</p>
          </div>
        </div>
      </div>

      <div className="filter-bar">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Search subjects by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {showAddForm && (
        <div className="add-subject-form">
          <h3>Create New Subject</h3>
          <form onSubmit={handleAddSubject}>
            <div className="form-group">
              <label>Subject Name *</label>
              <input
                type="text"
                value={newSubject.name}
                onChange={(e) => setNewSubject({ ...newSubject, name: e.target.value })}
                placeholder="e.g., Mathematics, English Language, Visual Arts"
                required
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select
                value={newSubject.category}
                onChange={(e) => setNewSubject({ ...newSubject, category: e.target.value })}
              >
                {SUBJECT_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{getCategoryIcon(cat)} {cat}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newSubject.description}
                onChange={(e) => setNewSubject({ ...newSubject, description: e.target.value })}
                placeholder="Brief description of the subject..."
                rows={3}
              />
            </div>
            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={newSubject.isActive}
                  onChange={(e) => setNewSubject({ ...newSubject, isActive: e.target.checked })}
                />
                Active
              </label>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>Cancel</button>
              <button type="submit">Add Subject</button>
            </div>
          </form>
        </div>
      )}

      {filteredSubjects.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📖</div>
          <h3>{searchTerm ? 'No Matching Subjects' : 'No Subjects Yet'}</h3>
          <p>{searchTerm ? 'Try a different search term.' : 'Add subjects to organize your school\'s curriculum and course offerings.'}</p>
        </div>
      ) : (
        <div className="subjects-list">
          {filteredSubjects.map(subject => (
            <div
              key={subject.id}
              className="subject-card"
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedSubject(subject)}
            >
              <div className="subject-icon">{getCategoryIcon(subject.category)}</div>
              <div className="subject-info">
                <h4>{subject.name}</h4>
                <div className="subject-meta">
                  <span className="subject-category">{subject.category}</span>
                  {subject.isActive === false
                    ? <span className="inactive-badge">Inactive</span>
                    : <span className="active-badge">Active</span>
                  }
                </div>
                {subject.description && <p className="subject-description">{subject.description}</p>}
              </div>
              <div className="subject-actions">
                <button
                  className="toggle-button"
                  onClick={(e) => { e.stopPropagation(); handleToggleActive(subject); }}
                  title={subject.isActive !== false ? 'Deactivate' : 'Activate'}
                >
                  {subject.isActive !== false ? '✅ Active' : '⏸️ Inactive'}
                </button>
                <button className="edit-button" onClick={(e) => { e.stopPropagation(); handleEditClick(subject); }}>
                  ✏️ Edit
                </button>
                <button className="delete-button" onClick={(e) => { e.stopPropagation(); handleDeleteSubject(subject); }}>
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingSubject && (
        <div className="modal-overlay" onClick={() => setEditingSubject(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Subject</h3>
            <div className="form-group">
              <label>Subject Name</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
              >
                {SUBJECT_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{getCategoryIcon(cat)} {cat}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="form-group toggle">
              <label>
                <input
                  type="checkbox"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                />
                Active
              </label>
            </div>
            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setEditingSubject(null)}>Cancel</button>
              <button className="save-button" onClick={handleSaveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSubjectManagementView;
